import React, { useState, useEffect } from 'react';
import {
  AdminContainer,
  AdminContainerCentered,
  AdminFlexRow,
  FlexRowButtonRow,
  StyledFormControl,
  StyledTable,
  FlexRowPad20,
} from './styles';
import { get, size, set } from 'global/lib/lodash';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { FormControl, InputLabel, MenuItem, Select, Button, TableContainer, TableHead, TableBody, TableRow, TableCell, Checkbox, Table, IconButton } from '@mui/material';
import { stopProp } from 'global/utils';
import { getRealmIcon } from 'global/components/droprealms';
import { withRouter } from 'react-router-dom';
import sendData from 'redux/actions/sendData';
import { Loader, useScreen } from 'global/styles';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import Alert from '@mui/material/Alert';
import { useSelector } from 'react-redux';
import { DropDown } from 'global/components/dropdown';
import copy from 'copy-to-clipboard';
import { IconButton as AppIconButton } from 'global/components/Button';
import { buildCreateFromTemplateUrl } from 'feature/report/AssessmentCreateFromTemplate/createFromTemplateConfig';

// PFM-2545: per-row copy-link button for the static create-from-template link.
// The clipboard write is synchronous (no await before copy()) so it stays
// iOS-safe, and we flash "Link Copied!" inline. stopProp keeps the row onClick
// (which opens the template editor) from also firing.
const CopyLinkButton = ({ templateId }) => {
  const [copied, setCopied] = useState(false);

  const onCopy = (e) => {
    stopProp(e);
    const url = buildCreateFromTemplateUrl(templateId);
    if (url) copy(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return copied ? (
    <span style={{ whiteSpace: 'nowrap' }}>Link Copied!</span>
  ) : (
    <AppIconButton variant="text" color="secondary" toolTip="Copy create link" onClick={onCopy} icon={<LinkIcon fontSize="small" />} />
  );
};

export default withRouter((props) => {
  const { isMobile } = useScreen();
  const [error, setError] = useState();
  const [state, setState] = useState({
    scorecards: [],
    scValue: 0,
    status: [
      { title: 'Default', value: 'template' },
      { title: 'Global', value: 'global_template' },
    ],
    statusValue: 0,
    selected: {},
    loading: false,
  });
  const realm = useSelector((state) => get(state, 'auth.realm.id'));
  const templates = useRetrieveData(
    { url: '/assessment-templates' },
    {
      build: ['assessments'],
      useEffect: true,
      params: {
        include: 'scorecards,realm,authors',
        filter: realm && { realm },
      },
    }
  );

  const temps = get(templates, 'data', []);

  useEffect(() => {
    if (size(temps)) {
      const scorecards = {};
      temps.map((template) => {
        template.scorecards.map((sc) => {
          set(scorecards, get(sc, 'id'), sc);
        });
      });

      setState((s) => ({
        ...s,
        scorecards: Object.keys(scorecards).map((k) => ({ ...scorecards[k] })),
      }));
    }
  }, [temps]);

  const data = temps.filter((t) => {
    let passes = true;
    if (state.scValue) {
      passes = get(t, 'scorecards', []).find((sc) => sc.id === state.scValue);
    }

    if (passes && state.statusValue) {
      passes = get(t, 'status', '') === state.statusValue;
    }

    return passes;
  });

  const checkChange = (row, e) => {
    stopProp(e);
    const selected = { ...state.selected };
    const isSel = !get(selected, row.id);
    set(selected, row.id, isSel);
    setState((s) => ({ ...s, selected }));
  };

  const selectRow = (row) => {
    props.history.push(`/reports/assessment-template/${row.id}`, { from: 'account/content' });
  };

  const processSelected = async (status = 'template') => {
    try {
      setState((s) => ({ ...s, loading: true }));
      await Promise.all(
        Object.keys(state.selected)
          .filter((k) => state.selected[k])
          .map(async (k) => {
            const item = data.find((d) => d.id === k);
            if (item) {
              const id = get(item, 'id').toString();
              await sendData({
                method: 'patch',
                url: `/assessment-templates/${id}`,
                data: {
                  data: {
                    type: 'assessment-templates',
                    id,
                    attributes: {
                      status,
                    },
                  },
                },
              });
            }
          })
      );
      await templates.update();
    } catch (err) {
      setError(`Failed to update: ${err.message}`);
    } finally {
      setState((s) => ({ ...s, loading: false }));
    }
  };

  const hasSelected = size(Object.keys(state.selected).filter((k) => !!state.selected[k]));

  return state.loading ? (
    <AdminContainerCentered isMobile={isMobile}>
      <Loader />
    </AdminContainerCentered>
  ) : (
    <AdminContainer isMobile={isMobile}>
      {error && (
        <Alert
          severity="error"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => {
                setError();
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}

      <AdminFlexRow>
        <StyledFormControl>
          <DropDown
            label="Scorecard"
            value={state.scValue}
            onChange={(e) => setState((s) => ({ ...s, scValue: e.target.value }))}
            options={[{ id: 0, name: 'Any' }].concat(
              state.scorecards.map((sc) => ({
                id: get(sc, 'id'),
                name: get(sc, 'name'),
              }))
            )}
          />
        </StyledFormControl>

        <StyledFormControl>
          <DropDown
            label="Status"
            value={state.statusValue}
            onChange={(e) => setState((s) => ({ ...s, statusValue: e.target.value }))}
            options={[{ id: 0, name: 'Any' }].concat(
              state.status.map((sc) => ({
                id: sc.value,
                name: sc.title,
              }))
            )}
          />
        </StyledFormControl>
      </AdminFlexRow>

      {!!hasSelected && (
        <FlexRowButtonRow>
          <Button color="secondary" variant="contained" onClick={() => processSelected('global_template')}>
            MAKE GLOBAL
          </Button>
          <Button color="secondary" variant="contained" onClick={() => processSelected('template')}>
            REMOVE FROM GLOBAL
          </Button>
        </FlexRowButtonRow>
      )}

      <StyledTable>
        {templates.loading && (
          <FlexRowPad20 isMobile={isMobile}>
            <Loader />
          </FlexRowPad20>
        )}
        {!templates.loading && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox"></TableCell>
                  {COLUMNS.map((col) => (
                    <TableCell key={col.field} size="small" width={col.width}>
                      {col.title}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((temp) => {
                  return (
                    <TableRow key={temp.id} hover onClick={() => selectRow(temp)}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={!!get(state, `selected.${temp.id}`)} onClick={(e) => checkChange(temp, e)} />
                      </TableCell>
                      {COLUMNS.map((col) => {
                        const val = col.render ? col.render(temp, col) : get(temp, col.field);
                        return (
                          <TableCell key={col.field} size={'small'} width={col.width}>
                            {val}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </StyledTable>
    </AdminContainer>
  );
});

const COLUMNS = [
  { field: 'name', title: 'Title', width: 200 },
  {
    field: 'realm.name',
    title: 'Location',
    width: 200,
    render: (row) => {
      const Rcon = getRealmIcon(row.realm);
      return (
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <div style={{ marginRight: 5 }}>{Rcon}</div>
          {get(row, 'realm.name')}
        </div>
      );
    },
  },
  { field: 'authors.0.name', title: 'Author', width: 150 },
  {
    field: 'status',
    title: 'Global',
    width: 100,
    render: (row, props) => {
      return row.status === 'global_template' ? 'Yes' : 'No';
    },
  },
  {
    field: 'createLink',
    title: 'Link',
    width: 100,
    render: (row) => <CopyLinkButton templateId={get(row, 'id')} />,
  },
];
